import React, { useState, useEffect } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Button } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { db } from "../../firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { ScaleLoader } from "react-spinners";
import { FaStar, FaHome, FaMapMarkerAlt, FaWhatsapp, FaPhoneAlt, FaUserCircle, FaShieldAlt } from "react-icons/fa";

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1280 },
    items: 4,
  },
  desktopMedium: {
    breakpoint: { max: 1280, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 3,
  },
  mobile: {
    breakpoint: { max: 768, min: 464 },
    items: 2,
  },
  smallMobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

const FeaturedSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoading(true);
        const sellersRef = collection(db, "Featured-Agents");
        const qSnap = await getDocs(sellersRef);
        const sellersArray = [];
        qSnap.forEach((doc) => {
          sellersArray.push({ id: doc.id, ...doc.data() });
        });
        setSellers(sellersArray);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchSellers();
  }, []);

  // If there are 3 or fewer sellers, center them using flexbox instead of carousel
  const shouldUseFlex = sellers.length <= 3 && sellers.length > 0;

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <FaShieldAlt className="text-emerald-600 text-2xl" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Featured Real Estate Agents
          </h2>
          <div className="w-24 h-1 bg-emerald-500 mx-auto mb-4 rounded-full"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Connect with Pakistan's most trusted and verified real estate professionals
          </p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <ScaleLoader color="#10b981" />
          </div>
        ) : sellers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaHome className="text-gray-400 text-2xl" />
            </div>
            <p className="text-gray-500">No featured agents available</p>
          </div>
        ) : shouldUseFlex ? (
          // Centered flex layout for 3 or fewer items
          <div className="flex justify-center items-center gap-6 flex-wrap">
            {sellers.map((seller) => (
              <div 
                key={seller.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full sm:w-[280px] md:w-[300px] border border-gray-100 group"
              >
                {/* Image Section */}
                <div className="relative h-40 w-full overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100">
                  {seller.profileImage || seller.thumbnail ? (
                    <img
                      src={seller.profileImage || seller.thumbnail}
                      alt={seller.sellerName}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500">
                      <FaUserCircle className="text-white text-5xl" />
                    </div>
                  )}
                  
                  {/* Verified Badge */}
                  <div className="absolute top-3 left-3">
                    <div className="flex items-center gap-1 bg-emerald-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
                      <FaStar className="text-yellow-400 text-[10px]" />
                      Verified
                    </div>
                  </div>
                  
                  {/* Rating Badge */}
                  <div className="absolute top-3 right-3">
                    <div className="flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-md">
                      <FaStar className="text-yellow-500 text-[10px]" />
                      <span className="text-xs font-bold text-gray-700">{seller.rating || "4.8"}</span>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-4">
                  <h3 className="text-base font-bold text-gray-800 mb-1 text-center line-clamp-1">
                    {seller.sellerName}
                  </h3>
                  <p className="text-gray-500 text-xs mb-3 text-center flex items-center justify-center gap-1">
                    <FaMapMarkerAlt className="text-emerald-500" size={10} />
                    <span className="line-clamp-1">{seller.address || "Location not specified"}</span>
                  </p>
                  
                  {/* Stats */}
                  <div className="flex justify-between items-center mb-3 px-2">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Properties</div>
                      <div className="text-sm font-bold text-emerald-600">
                        {seller.listingsCount || seller.properties || "50+"}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Experience</div>
                      <div className="text-sm font-bold text-emerald-600">
                        {seller.experience || "5+ yrs"}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Button */}
                <div className="px-4 pb-4">
                  <Link to={`/seller-profile/${seller.id}`}>
                    <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg transition-all duration-300 text-sm shadow-md hover:shadow-lg">
                      View Properties
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Carousel for 4+ items
          <>
            <div className="flex justify-center">
              <div className="w-full max-w-7xl">
                <Carousel 
                  responsive={responsive}
                  swipeable={true}
                  draggable={true}
                  showDots={true}
                  infinite={sellers.length > 4}
                  autoPlay={sellers.length > 1}
                  autoPlaySpeed={4000}
                  keyBoardControl={true}
                  customTransition="all .5s ease-in-out"
                  transitionDuration={500}
                  containerClass="carousel-container pb-12"
                  removeArrowOnDeviceType={["tablet", "mobile"]}
                  dotListClass="custom-dot-list-style"
                  itemClass="px-3"
                >
                  {sellers.map((seller) => (
                    <div 
                      key={seller.id}
                      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col border border-gray-100 group"
                    >
                      {/* Image Section */}
                      <div className="relative h-40 w-full overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100">
                        {seller.profileImage || seller.thumbnail ? (
                          <img
                            src={seller.profileImage || seller.thumbnail}
                            alt={seller.sellerName}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500">
                            <FaUserCircle className="text-white text-5xl" />
                          </div>
                        )}
                        
                        {/* Verified Badge */}
                        <div className="absolute top-3 left-3">
                          <div className="flex items-center gap-1 bg-emerald-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
                            <FaStar className="text-yellow-400 text-[10px]" />
                            Verified
                          </div>
                        </div>
                        
                        {/* Rating Badge */}
                        <div className="absolute top-3 right-3">
                          <div className="flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-md">
                            <FaStar className="text-yellow-500 text-[10px]" />
                            <span className="text-xs font-bold text-gray-700">{seller.rating || "4.8"}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="p-4 flex-grow">
                        <h3 className="text-base font-bold text-gray-800 mb-1 text-center line-clamp-1">
                          {seller.sellerName}
                        </h3>
                        <p className="text-gray-500 text-xs mb-3 text-center flex items-center justify-center gap-1">
                          <FaMapMarkerAlt className="text-emerald-500" size={10} />
                          <span className="line-clamp-1">{seller.address || "Location not specified"}</span>
                        </p>
                        
                        {/* Stats */}
                        <div className="flex justify-between items-center mb-3 px-2">
                          <div className="text-center">
                            <div className="text-xs text-gray-500">Properties</div>
                            <div className="text-sm font-bold text-emerald-600">
                              {seller.listingsCount || seller.properties || "50+"}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500">Experience</div>
                            <div className="text-sm font-bold text-emerald-600">
                              {seller.experience || "5+ yrs"}
                            </div>
                          </div>
                        </div>
                        
                      </div>
                      
                      {/* Button */}
                      <div className="px-4 pb-4">
                        <Link to={`/seller-profile/${seller.id}`}>
                          <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 rounded-lg transition-all duration-300 text-sm shadow-md hover:shadow-lg">
                            View Properties
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>
            </div>
          </>
        )}
        
        {/* CTA Button */}
        {sellers.length > 0 && (
          <div className="flex justify-center mt-10">
            <Link to="/featured-agents">
              <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 inline-flex items-center gap-2 text-sm">
                Browse All Agents
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            </Link>
          </div>
        )}
      </div>
      
      <style jsx>{`
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
        
        :global(.custom-dot-list-style) {
          position: absolute;
          bottom: -5px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 8px;
        }
        
        :global(.custom-dot-list-style button) {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #d1d5db;
          transition: all 0.3s;
          padding: 0;
        }
        
        :global(.custom-dot-list-style .react-multi-carousel-dot--active button) {
          background-color: #10b981;
          width: 24px;
          border-radius: 4px;
        }
        
        :global(.react-multi-carousel-list) {
          padding-bottom: 15px;
        }
        
        :global(.react-multi-carousel-arrow) {
          background-color: #10b981;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          min-width: 36px;
          opacity: 0.9;
        }
        
        :global(.react-multi-carousel-arrow:hover) {
          background-color: #059669;
          opacity: 1;
        }
        
        :global(.react-multi-carousel-arrow::before) {
          font-size: 16px;
          color: white;
        }
        
        :global(.react-multi-carousel-arrow--left) {
          left: calc(1% + 1px);
        }
        
        :global(.react-multi-carousel-arrow--right) {
          right: calc(1% + 1px);
        }
      `}</style>
    </section>
  );
};

export default FeaturedSellers;